import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, Navigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { doc, getDoc, setDoc, serverTimestamp, type DocumentSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import FtuTopNav from "../components/FtuTopNav";
import FtuQuestionCard from "../components/FtuQuestionCard";
import { AnimatePresence, motion } from "framer-motion";
import { IconCheck } from "@tabler/icons-react";
import { useWeekContext } from "../context/WeekContext";

interface Question {
  id: string;
  question: string;
  options: string[];
  optionDescriptions?: Record<string, string>;
}

interface WeekData {
  journalPrompt: string;
  questions: Question[];
  theme?: { intro: string };
}

const PAGE_VARIANTS = {
  initial: { opacity: 0, y: 8 },
  enter: { opacity: 1, y: 0, transition: { duration: 0.2, ease: "easeOut" as const } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.15, ease: "easeIn" as const } },
};

const Questionnaire: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { weekId } = useParams<{ weekId: string }>();

  const [weekData, setWeekData] = useState<WeekData | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [showIntro, setShowIntro] = useState(true);

  const { unlockedWeek, loading: lockLoading } = useWeekContext();

  const requestedWeekNumber = useMemo(() => {
    if (!weekId) return null;
    const match = weekId.match(/^week_(\d+)$/);
    const num = match ? parseInt(match[1], 10) : null;
    
    // EXCLUSION: Week 6 does not have a questionnaire.
    if (num === 6) return null; 
    
    return num;
  }, [weekId]);

  const isUnauthorized =
    !lockLoading &&
    (requestedWeekNumber === null || requestedWeekNumber > unlockedWeek);

  const currentQuestion = useMemo(
    () => weekData?.questions[currentIndex] ?? null,
    [weekData, currentIndex]
  );

  useEffect(() => {
    if (!user || !weekId || requestedWeekNumber === null) return;

    const loadWeekAndAnswers = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const weekRef = doc(db, "program", weekId);
        const answersRef = doc(db, "questionnaires", user.uid, "video_responses", weekId);

        const fetches: Promise<DocumentSnapshot>[] = [getDoc(weekRef), getDoc(answersRef)];
        if (weekId === "week_1") {
          fetches.push(getDoc(doc(db, "questionnaires", user.uid, "responses", "FTU.A.2")));
        }

        const [weekSnap, answersSnap, ftuAnswerSnap] = await Promise.all(fetches);
        const weekDataRaw = weekSnap.exists() ? (weekSnap.data() as WeekData) : null;

        if (answersSnap.exists()) {
          setAnswers(answersSnap.data() as Record<string, string>);
        } else {
          setAnswers({});
        }

        if (!weekDataRaw) {
          setWeekData(null);
          setLoadError("This weekly questionnaire is not available yet.");
          return;
        }

        let questions = weekDataRaw.questions || [];

        if (weekId === "week_1" && ftuAnswerSnap?.exists()) {
          const birthType: string = ftuAnswerSnap.data()?.answer ?? "";
          questions = birthType === "vaginal"
            ? questions.slice(0, 4)
            : questions.slice(4, 6);
        }

        setWeekData({ ...weekDataRaw, questions });
      } catch (err) {
        console.error("Error loading data:", err);
        setWeekData(null);
        setLoadError("Could not load this weekly questionnaire. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    loadWeekAndAnswers();
  }, [user, weekId, requestedWeekNumber]);

  // ---------------------------------------------------------------------------
  // Branching Logic
  // ---------------------------------------------------------------------------
  const getNextIndex = useCallback((currentIdx: number, currentAnswers: Record<string, string>): number => {
    const questions = weekData?.questions ?? [];
    const q = questions[currentIdx];
    if (!q) return currentIdx + 1;

    if (weekId === "week_1") {
      if (q.id === "question_1" && currentAnswers["question_1"] === "None") {
        return questions.length;
      }
      if (q.id === "question_2" && currentAnswers["question_2"] === "No") {
        return questions.findIndex((q) => q.id === "question_4");
      }
      if (q.id === "question_3" && currentAnswers["question_3"] === "Yes") {
        return questions.length;
      }
    }

    if (weekId === "week_2") {
      if (
        q.id === "question_2" &&
        (currentAnswers["question_2"] === "No" || currentAnswers["question_2"] === "I don't know")
      ) {
        return questions.findIndex((q) => q.id === "question_5");
      }
    }

    return currentIdx + 1;
  }, [weekData, weekId]);

  const getPrevIndex = (currentIdx: number): number => {
    const questions = weekData?.questions ?? [];

    if (weekId === "week_1") {
      if (questions[currentIdx]?.id === "question_4" && answers["question_2"] === "No") {
        return questions.findIndex((q) => q.id === "question_2");
      }
    }

    if (weekId === "week_2") {
      if (
        questions[currentIdx]?.id === "question_5" &&
        (answers["question_2"] === "No" || answers["question_2"] === "I don't know")
      ) {
        return questions.findIndex((q) => q.id === "question_2");
      }
    }

    return currentIdx - 1;
  };

  // ---------------------------------------------------------------------------
  // Dynamic Path Calculations (Switches Questionnaire to Dynamic Numbering)
  // ---------------------------------------------------------------------------
  const currentPath = useMemo(() => {
    const path: number[] = [];
    if (!weekData?.questions || weekData.questions.length === 0) return path;

    let idx = 0;
    const safetyCap = weekData.questions.length + 1;
    let iterations = 0;

    while (idx < weekData.questions.length && iterations < safetyCap) {
      path.push(idx);
      idx = getNextIndex(idx, answers);
      iterations++;
    }
    return path;
  }, [weekData, answers, getNextIndex]);

  const currentStepNumber = useMemo(() => {
    const pathIndex = currentPath.indexOf(currentIndex);
    return pathIndex !== -1 ? pathIndex + 1 : currentIndex + 1;
  }, [currentPath, currentIndex]);

  const maxStepsNumber = useMemo(() => {
    return currentPath.length > 0 ? currentPath.length : (weekData?.questions.length ?? 0);
  }, [currentPath, weekData]);

  const isLastQuestionInPath = useMemo(() => {
    if (currentPath.length === 0) return currentIndex === (weekData?.questions.length ?? 0) - 1;
    return currentPath.indexOf(currentIndex) === currentPath.length - 1;
  }, [currentPath, currentIndex, weekData]);

  // ---------------------------------------------------------------------------
  // Actions & Handlers
  // ---------------------------------------------------------------------------
  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const saveWeekAnswers = async () => {
    if (!user || !weekId) return;
    const weekDocRef = doc(db, "questionnaires", user.uid, "video_responses", weekId);
    try {
      await setDoc(weekDocRef, answers, { merge: true });
    } catch (err) {
      console.error("Error saving week answers:", err);
    }
  };

  const markQuestionnaireSubmitted = async () => {
    if (!user || !weekId) return;
    try {
      await setDoc(
        doc(db, "users", user.uid, "questionnaireSubmitted", weekId),
        { submittedAt: serverTimestamp() }
      );
    } catch (err) {
      console.error("Error marking questionnaire submitted:", err);
    }
  };

  const handleNext = () => {
    if (!currentQuestion) return;

    const nextIndex = getNextIndex(currentIndex, answers);

    if (nextIndex < (weekData?.questions.length ?? 0)) {
      setCurrentIndex(nextIndex);
    } else {
      setIsComplete(true);
      Promise.all([saveWeekAnswers(), markQuestionnaireSubmitted()])
        .catch((err) => console.error("Error on finish:", err));
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(getPrevIndex(currentIndex));
    } else {
      setShowIntro(true);
    }
  };

  const renderQuestion = () => {
    if (!currentQuestion) return null;

    return (
      <div className="flex flex-col gap-4 w-full">
        {currentQuestion.options.map((opt, i) => {
          const isSelected = answers[currentQuestion.id] === opt;
          const description = currentQuestion.optionDescriptions?.[opt];

          return (
            <button
              key={i}
              type="button"
              onClick={() => handleAnswerChange(currentQuestion.id, opt)}
              className={`w-full flex flex-col items-start px-5 py-4 rounded-2xl text-left transition-all border-2 ${
                isSelected
                  ? "bg-white border-[#1e5e5b] shadow-md"
                  : "bg-white border-transparent hover:border-gray-200"
              }`}
            >
              <div className="flex justify-between items-center w-full">
                <span
                  className={`text-lg font-semibold leading-tight ${isSelected ? "text-[#1e5e5b]" : "text-[#0c2624]"}`}
                  style={{ fontFamily: "DM Sans, sans-serif" }}
                >
                  {opt}
                </span>
                {isSelected && (
                  <IconCheck size={20} strokeWidth={2} className="text-[#1e5e5b] flex-shrink-0 ml-2" />
                )}
              </div>
              {description && (
                <p className="mt-1.5 text-sm text-gray-500 font-normal leading-snug">
                  {description}
                </p>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  // ── RENDER GUARDS ──────────────────────────────────────────────────────────

  if (!user) return <Navigate to="/login" />;

  // ── HIGH QUALITY BRANDED SPINNER ──
  if (lockLoading || (isLoading || !weekData && !loadError)) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100%',
        minHeight: '-webkit-fill-available', 
        backgroundColor: '#fcf9f7',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2147483647,
        pointerEvents: 'all'
      }}>
        {/* Spinner */}
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(30, 94, 91, 0.1)',
          borderTop: '4px solid #1e5e5b',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          marginBottom: '20px',
          boxSizing: 'border-box'
        }} />

        {/* Branding */}
        <div style={{
          fontFamily: 'DM Sans, sans-serif',
          textAlign: 'center'
        }}>
          <p style={{
            color: '#0c2624',
            fontSize: '18px',
            fontWeight: '600',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            margin: 0,
            animation: 'pulse 1.5s ease-in-out infinite'
          }}>
            BodyOwn
          </p>
        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          body { overflow: hidden !important; }
        `}</style>
      </div>
    );
  }

  if (isUnauthorized) return <Navigate to="/recovery" replace />;

  if (!isLoading && loadError) {
    return (
      <div className="min-h-[100dvh] bg-[#fcf9f7] flex flex-col items-center justify-center px-6 text-center gap-4">
        <p className="text-[#0c2624] text-base font-medium" style={{ fontFamily: "DM Sans, sans-serif" }}>
          {loadError}
        </p>
        <button
          type="button"
          onClick={() => navigate("/recovery")}
          className="h-11 px-5 rounded-full bg-[#1e5e5b] text-white font-semibold"
        >
          Back to Recovery
        </button>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Completion Screen
  // ---------------------------------------------------------------------------
  if (isComplete) {
    return (
      <div className="min-h-[100dvh] bg-[#fcf9f7] flex flex-col items-center justify-center px-6 text-center gap-6">
        <div className="w-16 h-16 rounded-full bg-[#1e5e5b] flex items-center justify-center">
          <IconCheck size={32} strokeWidth={2} color="white" />
        </div>
        <h2 className="text-2xl font-bold text-[#0c2624]" style={{ fontFamily: "DM Sans, sans-serif" }}>
          All done!
        </h2>
        <p className="text-base text-gray-500 leading-relaxed max-w-[320px]" style={{ fontFamily: "DM Sans, sans-serif" }}>
          Thank you for completing this week's check-in. Your responses help us tailor your recovery and will contribute to your 6-week GP summary.
        </p>
        <button
          type="button"
          onClick={() => navigate("/recovery")}
          className="h-12 px-8 rounded-full bg-[#1e5e5b] text-white font-semibold text-base"
          style={{ fontFamily: "DM Sans, sans-serif" }}
        >
          Back to Recovery
        </button>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Intro Screen
  // ---------------------------------------------------------------------------
  if (showIntro && weekData?.theme?.intro) {
    return (
      <div className="min-h-[100dvh] bg-[#fcf9f7] flex flex-col">
        <FtuTopNav
          canGoBack={false}
          onBack={() => {}}
          onExit={() => navigate("/recovery")}
          showProgress={false}
          currentStep={0}
          maxSteps={maxStepsNumber}
        />
        <div className="flex-1 flex flex-col justify-between w-full max-w-[680px] mx-auto px-6 pb-8 mt-6">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="flex flex-col gap-5"
          >
            <p
              className="text-xs font-semibold uppercase tracking-widest text-[#1e5e5b]"
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              This week's focus
            </p>
            <h1
              className="text-2xl font-bold text-[#0c2624] leading-snug"
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              Before we begin
            </h1>
            <p
              className="text-base text-gray-600 leading-relaxed"
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              {weekData.theme.intro.trim()}
            </p>
            <p
              className="text-sm text-gray-400 leading-relaxed"
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              These questions help us personalise your recovery plan and build your 6-week summary. 
              There are no right or wrong answers — just answer as best you can.
            </p>
          </motion.div>

          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.2 }}
            whileTap={{ scale: 0.98 }}
            type="button"
            onClick={() => setShowIntro(false)}
            className="w-full h-12 rounded-full bg-[#1e5e5b] text-white font-semibold text-base mt-10"
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            Let's begin
          </motion.button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[#fcf9f7] flex flex-col">
      <FtuTopNav
        canGoBack
        onBack={handleBack}
        onExit={() => navigate("/dashboard")}
        showProgress
        currentStep={currentStepNumber}
        maxSteps={maxStepsNumber}
      />

      <div className="w-full max-w-[680px] mx-auto px-6 mt-6">
        <AnimatePresence mode="wait">
          {currentQuestion && (
            <motion.div
              key={currentQuestion.id}
              variants={PAGE_VARIANTS}
              initial="initial"
              animate="enter"
              exit="exit"
            >
              <FtuQuestionCard question={currentQuestion.question}>
                {renderQuestion()}
              </FtuQuestionCard>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {currentQuestion && (
        <div className="w-full max-w-[680px] mx-auto px-6 mt-6 pb-6">
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={handleNext}
            disabled={!answers[currentQuestion.id]}
            className={`w-full h-12 px-4 rounded-full font-semibold text-base transition-all ${
              !answers[currentQuestion.id]
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-[#1e5e5b] text-white hover:opacity-90 shadow-sm"
            }`}
          >
            {isLastQuestionInPath ? "Finish" : "Next"}
          </motion.button>
        </div>
      )}
    </div>
  );
};

export default Questionnaire;